import { useCallback, useContext } from 'react'
import { ProjectContext } from '../../contexts'
import api from '../../api'
import { DataTable, Tag } from '../../ui'
import { useTranslation } from 'react-i18next'
import { useResolver } from '../../hooks'
import * as stepTypes from '../journey/steps'
import { stepCategoryColors } from '../journey/editor/JourneyEditorUtils'
import clsx from 'clsx'
import { camelToTitle } from '../../utils'
import { TagProps } from '../../ui/Tag'
import Modal, { ModalProps } from '../../ui/Modal'
import { JourneyUserStep } from '../../types'
import { useNavigate } from 'react-router'

interface EntranceTableParams {
    entranceId: number
}

export const typeVariants: Record<string, TagProps['variant']> = {
    completed: 'success',
    error: 'error',
    action: 'info',
    delay: 'warn',
    pending: 'plain',
}

export function JourneyUserEntranceTable({ entranceId }: EntranceTableParams) {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const [project] = useContext(ProjectContext)

    const resolver = useCallback(async () => await api.journeys.entrances.log(project.id, entranceId), [project.id, entranceId])
    const [result] = useResolver(resolver)
    const userSteps = result?.userSteps ?? []

    return (
        <DataTable
            items={userSteps ?? []}
            isLoading={!userSteps}
            columns={[
                {
                    key: 'step',
                    cell: ({ item }) => {

                        const stepType = stepTypes[item.step!.type as keyof typeof stepTypes]

                        return (
                            <div className="multi-cell">
                                <div className={clsx('icon-box', stepCategoryColors[stepType.category])}>
                                    {stepType?.icon}
                                </div>
                                <div className="text">
                                    <div className="title">{item.step!.name || t('untitled')}</div>
                                    <div className="subtitle">{t(item.step!.type)}</div>
                                </div>
                            </div>
                        )
                    },
                },
                {
                    key: 'type',
                    title: t('type'),
                    cell: ({ item }) => (
                        <Tag variant={typeVariants[item.type]}>
                            {camelToTitle(item.type)}
                        </Tag>
                    ),
                },
                { key: 'created_at', title: t('created_at') },
                { key: 'delay_until', title: t('delay_until') },
            ]}
            onSelectRow={async (row) => {
                await navigate(`/projects/${project.id}/journeys/${result?.journey.id}?step=${row.step?.id}`)
            }}
        />
    )
}

export type EntranceStep = Pick<JourneyUserStep, 'entrance_id' | 'journey' | 'user'>
type EntranceModalParams = {
    step?: EntranceStep
} & Omit<ModalProps, 'title' | 'open'>

export default function JourneyUserEntranceModal({ step, onClose }: EntranceModalParams) {

    const user = step?.user
    const journey = step?.journey

    return (
        <Modal title={`${user?.full_name ?? user?.external_id} - ${journey?.name}`}
            open={!!step}
            onClose={() => onClose(false)}
            size="large">
            {step && <JourneyUserEntranceTable entranceId={step.entrance_id} />}
        </Modal>
    )
}
